import { useQuery } from '@tanstack/react-query';
import { apiRequests } from '../../../shared/api/apiRequests';
import { useEffect, useState, useRef } from 'react';
import { Check, X, Plus, Minus, Search } from 'lucide-react';
import './Availability.css';

export const Availability = ({ onSelect, onSelectAllowed, onSelectDisallowed, initialValues }) => {
  const { data } = useQuery({
    queryKey: ['getCountries'],
    queryFn: () => apiRequests.countries.get(),
    staleTime: Infinity,
    cacheTime: Infinity,
  });
  
  const [isAllSelected, setAllSelected] = useState(initialValues?.availableInAllCountries ?? true);
  const [selectedAllowedCountries, setSelectedAllowedCountries] = useState([]);
  const [selectedDisallowedCountries, setSelectedDisallowedCountries] = useState([]);
  const [allowedOpen, setAllowedOpen] = useState(false);
  const [disallowedOpen, setDisallowedOpen] = useState(false);
  const isInitialized = useRef(false);
  const allowedRef = useRef(null);
  const disallowedRef = useRef(null);

  const getName = (value) => {
    return data?.items?.find((i) => String(i.id) === value)?.title || '';
  };

  const handleCheckboxChange = (value) => {
    setSelectedAllowedCountries((prevOptions) => {
      const newValue = prevOptions.includes(value)
        ? prevOptions.filter((option) => option !== value)
        : [...prevOptions, value];
      onSelectAllowed(newValue);
      return newValue;
    });
  };

  const handleCheckboxDisallowedCountries = (value) => {
    setSelectedDisallowedCountries((prevOptions) => {
      const newValue = prevOptions.includes(value)
        ? prevOptions.filter((option) => option !== value)
        : [...prevOptions, value];
      onSelectDisallowed(newValue);
      return newValue;
    });
  };

  useEffect(() => {
    if (data && initialValues && !isInitialized.current) {
      isInitialized.current = true;
      
      const isAvailableInAll = initialValues.availableInAllCountries === 'false' 
        ? false 
        : Boolean(initialValues.availableInAllCountries);
      setAllSelected(isAvailableInAll);
      
      if (Array.isArray(initialValues.allowedCountriesIds)) {
        setSelectedAllowedCountries(initialValues.allowedCountriesIds.map(id => id.toString()));
      }
      
      if (Array.isArray(initialValues.disallowedCountriesIds)) {
        setSelectedDisallowedCountries(initialValues.disallowedCountriesIds.map(id => id.toString()));
      }
    }
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (allowedRef.current && !allowedRef.current.contains(event.target)) {
        setAllowedOpen(false);
      }
      if (disallowedRef.current && !disallowedRef.current.contains(event.target)) {
        setDisallowedOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isAllCountries = isAllSelected === true || isAllSelected === 'true';

  return (
    <div className="availability">
      <div className="availability-toggle">
        <span className="availability-toggle-label">Доступно во всех странах</span>
        <div className="availability-switch-group">
          <button
            type="button"
            className={`availability-switch-btn ${isAllCountries ? 'active' : ''}`}
            onClick={() => { setAllSelected(true); onSelect(true); }}
          >
            Да
          </button>
          <button
            type="button"
            className={`availability-switch-btn ${!isAllCountries ? 'active' : ''}`}
            onClick={() => { setAllSelected(false); onSelect(false); }}
          >
            Нет
          </button>
        </div>
      </div>

      <div className="availability-sections">
        <div className="availability-section availability-section--allowed" ref={allowedRef}>
          <div className="availability-section-header">
            <div className="availability-section-icon allowed"><Check size={14} /></div>
            <div className="availability-section-info">
              <span className="availability-section-label">Разрешено в</span>
              {selectedAllowedCountries.length > 0 ? (
                <div className="availability-tags">
                  {selectedAllowedCountries.map((id) => (
                    <span key={id} className="availability-tag allowed">
                      {getName(id)}
                      <button 
                        type="button"
                        className="availability-tag-remove"
                        onClick={() => handleCheckboxChange(id)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <span className="availability-section-empty">Все страны (по умолчанию)</span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="availability-dropdown-btn"
            onClick={() => setAllowedOpen(!allowedOpen)}
          >
            {allowedOpen ? <Minus size={16} /> : <Plus size={16} />}
          </button>
          {allowedOpen && (
            <div className="availability-dropdown">
              <div className="availability-dropdown-search">
                <input type="text" placeholder="Поиск страны..." className="availability-search-input" />
              </div>
              <div className="availability-dropdown-list">
                {data?.items?.map((option) => (
                  <label key={option.id} className="availability-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedAllowedCountries.includes(option.id.toString())}
                      onChange={() => handleCheckboxChange(option.id.toString())}
                    />
                    <span className="availability-checkbox-mark"></span>
                    <span className="availability-checkbox-label">{option.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="availability-section availability-section--disallowed" ref={disallowedRef}>
          <div className="availability-section-header">
            <div className="availability-section-icon disallowed"><X size={14} /></div>
            <div className="availability-section-info">
              <span className="availability-section-label">Запрещено в</span>
              {selectedDisallowedCountries.length > 0 ? (
                <div className="availability-tags">
                  {selectedDisallowedCountries.map((id) => (
                    <span key={id} className="availability-tag disallowed">
                      {getName(id)}
                      <button 
                        type="button"
                        className="availability-tag-remove"
                        onClick={() => handleCheckboxDisallowedCountries(id)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <span className="availability-section-empty">Нет ограничений</span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="availability-dropdown-btn"
            onClick={() => setDisallowedOpen(!disallowedOpen)}
          >
            {disallowedOpen ? <Minus size={16} /> : <Plus size={16} />}
          </button>
          {disallowedOpen && (
            <div className="availability-dropdown">
              <div className="availability-dropdown-search">
                <input type="text" placeholder="Поиск страны..." className="availability-search-input" />
              </div>
              <div className="availability-dropdown-list">
                {data?.items?.map((option) => (
                  <label key={option.id} className="availability-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedDisallowedCountries.includes(option.id.toString())}
                      onChange={() => handleCheckboxDisallowedCountries(option.id.toString())}
                    />
                    <span className="availability-checkbox-mark"></span>
                    <span className="availability-checkbox-label">{option.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
